import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import { buildClinicOrganizationGraph } from '@/lib/clinicSchema';
import { decorateSeoDescription, decorateSeoTitle, getClinicInfo } from '@/lib/clinicInfo';
import {
  SUPPORTED_LANGUAGES,
  SEO_BASE_URL as BASE_URL,
  removeLanguagePrefix,
  buildUrlWithLang as buildUrlWithLangShared,
  type SupportedLang,
} from '@/lib/seoUrls';
import { fitText, SEO_LIMITS } from '@/lib/seoLimits';

interface SchemaData {
  type: 'MedicalProcedure' | 'FAQPage' | 'HowTo' | 'Article' | 'LocalBusiness' | 'BreadcrumbList' | 'MedicalClinic' | 'AggregateRating' | 'ImageGallery' | 'CollectionPage' | 'WebApplication' | 'Service' | 'Physician';
  data: unknown;
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string[];
  canonicalUrl?: string;
  schemas?: SchemaData[];
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile' | 'book';
  noHreflang?: boolean;
  /** Keep the page out of search results (e.g. thank-you, search). Links are still followed. */
  noindex?: boolean;
}


export const SEOHead = ({ 
  title, 
  description, 
  keywords = [], 
  canonicalUrl,
  schemas = [],
  ogImage = 'https://hairartclinics.net/og-image.jpg',
  ogType = 'website',
  noHreflang = false,
  noindex = false
}: SEOHeadProps) => {
  const { language } = useLanguage();
  const location = useLocation();
  const BRAND = 'Hair Art Clinics';
  const clinic = getClinicInfo(language);
  const localizedTitle = title;
  const localizedDescription = description;
  const fullTitle = localizedTitle.includes(BRAND) ? localizedTitle : `${localizedTitle} | ${BRAND}`;
  
  // Get current path without language prefix
  const currentPathWithoutLang = removeLanguagePrefix(location.pathname);

  const buildUrlWithLang = (lang: SupportedLang | string): string =>
    buildUrlWithLangShared(currentPathWithoutLang, lang as SupportedLang, BASE_URL);

  // Canonical URL should be for current language
  const currentUrl = canonicalUrl || buildUrlWithLang(language);


  const getLocale = (lang: string) => {
    switch (lang) {
      case 'tr': return 'tr_TR';
      case 'nl': return 'nl_NL';
      case 'fr': return 'fr_FR';
      case 'ar': return 'ar_SA';
      default: return 'en_US';
    }
  };

  const getLanguageName = (lang: string) => {
    switch (lang) {
      case 'tr': return 'Turkish';
      case 'nl': return 'Dutch';
      case 'fr': return 'French';
      case 'ar': return 'Arabic';
      default: return 'English';
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={fitText(localizedDescription, SEO_LIMITS.metaDescription)} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Hreflang Tags for Multilingual SEO - Path-based */}
      {!noHreflang && SUPPORTED_LANGUAGES.map((lang) => (
        <link 
          key={lang}
          rel="alternate" 
          hrefLang={lang} 
          href={buildUrlWithLang(lang)} 
        />
      ))}
      {!noHreflang && (
        <link 
          rel="alternate" 
          hrefLang="x-default" 
          href={buildUrlWithLang('en')} 
        />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fitText(fullTitle, SEO_LIMITS.ogTitle)} />
      <meta property="og:description" content={fitText(localizedDescription, SEO_LIMITS.metaDescription)} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:locale" content={getLocale(language)} />
      {SUPPORTED_LANGUAGES.filter(l => l !== language).map((lang) => (
        <meta 
          key={lang}
          property="og:locale:alternate" 
          content={getLocale(lang)} 
        />
      ))}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fitText(fullTitle, SEO_LIMITS.twitterTitle)} />
      <meta name="twitter:description" content={fitText(localizedDescription, SEO_LIMITS.twitterDescription)} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Schema.org Structured Data */}
      {/* Canonical Organization + per-clinic MedicalClinic graph (single source, all languages) */}
      <script type="application/ld+json">
        {JSON.stringify(buildClinicOrganizationGraph(language))}
      </script>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema.data)}
        </script>
      ))}
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      <meta name="language" content={getLanguageName(language)} />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="Hair Art Clinics" />
      <meta name="geo.region" content="NL-ZH" />
      <meta name="geo.placename" content={clinic.city} />

      {/* Clinic NAP — auto-filled from the localized clinic record */}
      <meta name="business:contact_data:street_address" content={clinic.streetAddress} />
      <meta name="business:contact_data:locality" content={clinic.city} />
      <meta name="business:contact_data:postal_code" content={clinic.postalCode} />
      <meta name="business:contact_data:country_name" content={clinic.country} />
      <meta name="business:contact_data:phone_number" content={clinic.phone} />
      <meta name="business:contact_data:email" content={clinic.email} />
      <meta name="business:contact_data:website" content={clinic.siteUrl} />
      <meta name="business:hours" content={clinic.openingHours} />
      <meta property="place:location:opening_hours" content={clinic.openingHours} />
    </Helmet>
  );
};
